import { useState, useMemo, useRef, useEffect, useCallback } from 'react';

export default function ScripSelector({ symbols, selectedSymbol, onSymbolChange, loading }) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const allSymbols = useMemo(() => {
    if (!symbols) return [];
    return [
      ...symbols.indices.map((s) => ({ ...s, group: 'Indices' })),
      ...symbols.stocks.map((s) => ({ ...s, group: 'Stocks' })),
    ];
  }, [symbols]);

  const selectedLabel = useMemo(() => {
    if (!selectedSymbol) return '';
    const found = allSymbols.find((s) => s.symbol === selectedSymbol);
    return found ? `${found.symbol} — ${found.name}` : selectedSymbol;
  }, [selectedSymbol, allSymbols]);

  const filtered = useMemo(() => {
    if (!search) return allSymbols;
    const q = search.toLowerCase();
    return allSymbols.filter(
      (s) => s.symbol.toLowerCase().includes(q) || s.name.toLowerCase().includes(q)
    );
  }, [allSymbols, search]);

  // Reset highlight when filtered list changes
  useEffect(() => {
    setHighlightIndex(filtered.length > 0 ? 0 : -1);
  }, [filtered]);

  // Clear search when symbol is reset externally
  useEffect(() => {
    if (!selectedSymbol) {
      setSearch('');
      setIsOpen(false);
    }
  }, [selectedSymbol]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIndex < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll('[data-item]');
    if (items[highlightIndex]) {
      items[highlightIndex].scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIndex]);

  const handleSelect = useCallback((symbol) => {
    onSymbolChange(symbol);
    setSearch('');
    setIsOpen(false);
    setHighlightIndex(-1);
  }, [onSymbolChange]);

  const handleKeyDown = useCallback((e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightIndex >= 0 && highlightIndex < filtered.length) {
          handleSelect(filtered[highlightIndex].symbol);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearch('');
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  }, [isOpen, filtered, highlightIndex, handleSelect]);

  if (loading) {
    return <div className="animate-pulse h-9 bg-gray-200 dark:bg-gray-700 rounded w-full" />;
  }

  // Build flat list for rendering with group headers
  let itemIndex = 0;
  const groups = ['Indices', 'Stocks'].map((group) => {
    const items = filtered.filter((s) => s.group === group);
    if (items.length === 0) return null;
    const rendered = items.map((s) => {
      const idx = itemIndex++;
      return (
        <div
          key={s.symbol}
          data-item
          onClick={() => handleSelect(s.symbol)}
          onMouseEnter={() => setHighlightIndex(idx)}
          className={`px-3 py-2 text-sm cursor-pointer ${
            idx === highlightIndex
              ? 'bg-primary-50 dark:bg-primary-900/40 text-primary-700 dark:text-primary-400'
              : s.symbol === selectedSymbol
              ? 'bg-primary-50 dark:bg-primary-900/40 font-medium text-primary-700 dark:text-primary-400'
              : 'text-neutral-800 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-white/8'
          }`}
        >
          {s.symbol} — {s.name}
        </div>
      );
    });
    return (
      <div key={group}>
        <div className="px-3 py-1 text-xs font-semibold text-neutral-500 dark:text-neutral-400 bg-neutral-50 dark:bg-white/5 sticky top-0">
          {group}
        </div>
        {rendered}
      </div>
    );
  });

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">Symbol</label>
      <input
        ref={inputRef}
        type="text"
        placeholder={selectedSymbol ? selectedLabel : 'Search symbol...'}
        value={isOpen ? search : ''}
        onFocus={() => setIsOpen(true)}
        onChange={(e) => {
          setSearch(e.target.value);
          if (!isOpen) setIsOpen(true);
        }}
        onKeyDown={handleKeyDown}
        className="input-base focus-ring h-9"
      />
      {selectedSymbol && !isOpen && (
        <div className="absolute inset-y-0 top-5 right-2 flex items-center">
          <button
            onClick={() => {
              onSymbolChange('');
              setSearch('');
            }}
            className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 text-sm px-1"
          >
            &times;
          </button>
        </div>
      )}
      {isOpen && (
        <div
          ref={listRef}
          className="absolute z-20 w-full mt-1 bg-surface border border-neutral-200 dark:border-white/10 rounded-clay-sm shadow-clay dark:shadow-clay-dark max-h-60 overflow-auto"
        >
          {groups}
          {filtered.length === 0 && (
            <div className="px-3 py-3 text-sm text-neutral-400 dark:text-neutral-500 text-center">No matches</div>
          )}
        </div>
      )}
    </div>
  );
}
